import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import TextAlign from '@tiptap/extension-text-align';
import Placeholder from '@tiptap/extension-placeholder';
import { Table } from '@tiptap/extension-table';
import TableRow from '@tiptap/extension-table-row';
import TableHeader from '@tiptap/extension-table-header';
import TableCell from '@tiptap/extension-table-cell';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  Heading2,
  Heading3,
  List,
  ListOrdered,
  Link as LinkIcon,
  Image as ImageIcon,
  AlignLeft,
  AlignCenter,
  AlignRight,
  Code,
  Undo,
  Redo,
  Table2,
  Rows,
  Columns,
  Trash2,
} from 'lucide-react';
import { SignatureBlock } from './signatureBlock';

export interface RichEmailEditorHandle {
  /** Insert raw HTML at the caret (Visual) or append it (HTML mode). */
  insertHtml: (html: string) => void;
  focus: (position?: 'start' | 'end') => void;
}

interface RichEmailEditorProps {
  value: string;
  onChange: (html: string) => void;
  /** Min height of the editing surface, e.g. '240px' or 'calc(100vh - 280px)'. */
  minHeight?: string;
  /** Max height of the editing surface (scrolls beyond it). */
  maxHeight?: string;
  placeholder?: string;
  /** Extra controls rendered in the toolbar, left of the Visual/HTML switch. */
  toolbarExtras?: React.ReactNode;
  className?: string;
}

const RichEmailEditor = forwardRef<RichEmailEditorHandle, RichEmailEditorProps>(
  (
    {
      value,
      onChange,
      minHeight = '240px',
      maxHeight,
      placeholder = 'Write your message…',
      toolbarExtras,
      className,
    },
    ref,
  ) => {
    const [sourceMode, setSourceMode] = useState(false);
    const [sourceHtml, setSourceHtml] = useState(value);
    const [linkOpen, setLinkOpen] = useState(false);
    const [imageOpen, setImageOpen] = useState(false);
    const [linkUrl, setLinkUrl] = useState('');
    const [imageUrl, setImageUrl] = useState('');

    const editor = useEditor({
      extensions: [
        StarterKit.configure({ heading: { levels: [2, 3, 4] } }),
        Underline,
        Link.configure({
          openOnClick: false,
          HTMLAttributes: { class: 'text-primary underline underline-offset-2' },
        }),
        Image.configure({ HTMLAttributes: { class: 'rounded-md max-w-full' } }),
        TextAlign.configure({ types: ['heading', 'paragraph'] }),
        Placeholder.configure({ placeholder }),
        Table.configure({ resizable: true, HTMLAttributes: { class: 'rich-editor-table' } }),
        TableRow,
        TableHeader,
        TableCell,
        SignatureBlock,
      ],
      content: value,
      onUpdate: ({ editor: instance }) => {
        const html = instance.getHTML();
        setSourceHtml(html);
        onChange(html);
      },
      editorProps: {
        attributes: { class: 'rich-editor-content px-3 py-3 focus:outline-none' },
      },
    });

    // Sync external value changes (draft load, reply quote, AI draft, template insert).
    useEffect(() => {
      if (!editor) return;
      if (value === editor.getHTML()) return;
      editor.commands.setContent(value || '', { emitUpdate: false });
      setSourceHtml(value || '');
    }, [value, editor]);

    useImperativeHandle(ref, () => ({
      insertHtml: (html: string) => {
        if (sourceMode) {
          const next = `${sourceHtml || ''}${html}`;
          setSourceHtml(next);
          onChange(next);
          return;
        }
        editor?.chain().focus().insertContent(html).run();
      },
      focus: (position: 'start' | 'end' = 'end') => {
        if (sourceMode) return;
        editor?.commands.focus(position);
      },
    }), [editor, sourceMode, sourceHtml, onChange]);

    const toggleSource = useCallback(
      (next: boolean) => {
        if (!editor || next === sourceMode) return;
        if (next) {
          setSourceHtml(editor.getHTML());
        } else {
          editor.commands.setContent(sourceHtml || '', { emitUpdate: false });
          onChange(sourceHtml);
        }
        setSourceMode(next);
      },
      [editor, sourceMode, sourceHtml, onChange],
    );

    if (!editor) return null;

    const ToolBtn = ({
      active,
      onClick,
      children,
      title,
    }: {
      active?: boolean;
      onClick: () => void;
      children: React.ReactNode;
      title: string;
    }) => (
      <Button
        type="button"
        variant="ghost"
        size="icon"
        disabled={sourceMode}
        className={`h-8 w-8 ${
          active ? 'bg-accent text-accent-foreground' : 'text-muted-foreground hover:text-foreground'
        }`}
        onClick={onClick}
        title={title}
      >
        {children}
      </Button>
    );

    const Divider = () => <div className="mx-1 h-5 w-px bg-border" />;

    return (
      <div className={`overflow-hidden rounded-md border border-border/50 bg-card ${className || ''}`}>
        <div className="flex flex-wrap items-center gap-1 border-b border-border/50 bg-muted/30 px-2 py-1.5">
          <ToolBtn active={editor.isActive('bold')} onClick={() => editor.chain().focus().toggleBold().run()} title="Bold">
            <Bold className="h-4 w-4" />
          </ToolBtn>
          <ToolBtn active={editor.isActive('italic')} onClick={() => editor.chain().focus().toggleItalic().run()} title="Italic">
            <Italic className="h-4 w-4" />
          </ToolBtn>
          <ToolBtn active={editor.isActive('underline')} onClick={() => editor.chain().focus().toggleUnderline().run()} title="Underline">
            <UnderlineIcon className="h-4 w-4" />
          </ToolBtn>

          <Divider />

          <ToolBtn active={editor.isActive('heading', { level: 2 })} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} title="Heading 2">
            <Heading2 className="h-4 w-4" />
          </ToolBtn>
          <ToolBtn active={editor.isActive('heading', { level: 3 })} onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} title="Heading 3">
            <Heading3 className="h-4 w-4" />
          </ToolBtn>

          <Divider />

          <ToolBtn active={editor.isActive('bulletList')} onClick={() => editor.chain().focus().toggleBulletList().run()} title="Bullet List">
            <List className="h-4 w-4" />
          </ToolBtn>
          <ToolBtn active={editor.isActive('orderedList')} onClick={() => editor.chain().focus().toggleOrderedList().run()} title="Numbered List">
            <ListOrdered className="h-4 w-4" />
          </ToolBtn>

          <Divider />

          <ToolBtn active={editor.isActive({ textAlign: 'left' })} onClick={() => editor.chain().focus().setTextAlign('left').run()} title="Align Left">
            <AlignLeft className="h-4 w-4" />
          </ToolBtn>
          <ToolBtn active={editor.isActive({ textAlign: 'center' })} onClick={() => editor.chain().focus().setTextAlign('center').run()} title="Align Center">
            <AlignCenter className="h-4 w-4" />
          </ToolBtn>
          <ToolBtn active={editor.isActive({ textAlign: 'right' })} onClick={() => editor.chain().focus().setTextAlign('right').run()} title="Align Right">
            <AlignRight className="h-4 w-4" />
          </ToolBtn>

          <Divider />

          <ToolBtn
            active={editor.isActive('link')}
            onClick={() => {
              setLinkUrl(editor.getAttributes('link')?.href || '');
              setLinkOpen(true);
            }}
            title="Insert Link"
          >
            <LinkIcon className="h-4 w-4" />
          </ToolBtn>
          <ToolBtn onClick={() => { setImageUrl(''); setImageOpen(true); }} title="Insert Image">
            <ImageIcon className="h-4 w-4" />
          </ToolBtn>
          <ToolBtn active={editor.isActive('codeBlock')} onClick={() => editor.chain().focus().toggleCodeBlock().run()} title="Code Block">
            <Code className="h-4 w-4" />
          </ToolBtn>

          <Divider />

          <ToolBtn onClick={() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()} title="Insert Table">
            <Table2 className="h-4 w-4" />
          </ToolBtn>
          <ToolBtn onClick={() => editor.chain().focus().addRowAfter().run()} title="Add Row">
            <Rows className="h-4 w-4" />
          </ToolBtn>
          <ToolBtn onClick={() => editor.chain().focus().addColumnAfter().run()} title="Add Column">
            <Columns className="h-4 w-4" />
          </ToolBtn>
          <ToolBtn onClick={() => editor.chain().focus().deleteTable().run()} title="Delete Table">
            <Trash2 className="h-4 w-4" />
          </ToolBtn>

          <Divider />

          <ToolBtn onClick={() => editor.chain().focus().undo().run()} title="Undo">
            <Undo className="h-4 w-4" />
          </ToolBtn>
          <ToolBtn onClick={() => editor.chain().focus().redo().run()} title="Redo">
            <Redo className="h-4 w-4" />
          </ToolBtn>

          <div className="ml-auto flex items-center gap-1">
            {toolbarExtras}
            <Button
              type="button"
              size="sm"
              className="h-7 text-xs"
              variant={sourceMode ? 'ghost' : 'secondary'}
              onClick={() => toggleSource(false)}
            >
              Visual
            </Button>
            <Button
              type="button"
              size="sm"
              className="h-7 text-xs"
              variant={sourceMode ? 'secondary' : 'ghost'}
              onClick={() => toggleSource(true)}
            >
              HTML
            </Button>
          </div>
        </div>

        {sourceMode ? (
          <Textarea
            value={sourceHtml}
            onChange={(e) => {
              setSourceHtml(e.target.value);
              onChange(e.target.value);
            }}
            className="rounded-none border-0 bg-card font-mono text-xs focus-visible:ring-0"
            style={{ minHeight, maxHeight }}
          />
        ) : (
          <div className="overflow-y-auto" style={{ minHeight, maxHeight }}>
            <EditorContent editor={editor} />
          </div>
        )}

        <Dialog open={linkOpen} onOpenChange={setLinkOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Insert Link</DialogTitle>
            </DialogHeader>
            <Input
              value={linkUrl}
              onChange={(e) => setLinkUrl(e.target.value)}
              placeholder="https://example.com"
            />
            <DialogFooter>
              <Button
                type="button"
                onClick={() => {
                  if (linkUrl.trim()) {
                    editor.chain().focus().extendMarkRange('link').setLink({ href: linkUrl.trim() }).run();
                  } else {
                    editor.chain().focus().extendMarkRange('link').unsetLink().run();
                  }
                  setLinkOpen(false);
                }}
              >
                Apply
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={imageOpen} onOpenChange={setImageOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Insert Image</DialogTitle>
            </DialogHeader>
            <Input
              value={imageUrl}
              onChange={(e) => setImageUrl(e.target.value)}
              placeholder="https://example.com/image.jpg"
            />
            <DialogFooter>
              <Button
                type="button"
                disabled={!imageUrl.trim()}
                onClick={() => {
                  editor.chain().focus().setImage({ src: imageUrl.trim() }).run();
                  setImageOpen(false);
                }}
              >
                Insert
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  },
);

RichEmailEditor.displayName = 'RichEmailEditor';

export default RichEmailEditor;
